/**
 * Free grid generator (design.ts): resolves where each board ends, cuts the uprights a shelf runs
 * through, joins every junction and checks that every board is held.
 *
 * Junctions (ticks; a board end within `snap` of another board is taken to meet it):
 * - a shelf end at an upright butts into the upright's face (housed); if the upright ends there
 *   too (a corner), the upright is lengthened to cover the shelf's thickness and runs through;
 * - an upright end at a shelf that runs on past it stops on (or under) the shelf (housed);
 * - an upright end near the floor stands on the floor (or the feet), near the top reaches it;
 * - a shelf that runs right across an upright cuts it in two; each piece is housed into the shelf.
 *
 * Holding: each shelf needs its supports (uprights it butts into or rests on, or the floor).
 * Two or more: spans over the limit warn, overhangs past the outer supports are limited to the
 * cantilever ratio. One: a rated cantilever (a spine shelf), housed deep with a hidden steel
 * bracket, at most `MAX_REACH` past the upright, and the piece must be fixed to the wall. None:
 * an error with a fix that removes the shelf. Every board must connect to the floor.
 */
import type { BuildModel, Design, Issue } from '../../domain/schemas';
import { parseConstructionProfile, type ConstructionProfile } from '../../domain/profiles';
import { fmtMm, mm } from '../../domain/units';
import type { Hardware } from '../assembly';
import { baseErrors, baseOf, feetHardware, footKindFor, liftOf } from '../base';
import { assembleBuild, familyJoints, familyStock, layable, sizeErrors, type FamilyBoard, type FamilyJoint, type FamilyOpening } from '../build-common';
import { resolveStock, STOCKS, type AnyStock } from '../stock';
import jointsJson from './joints.json' with { type: 'json' };
import { FREE_ID, FREE_LABEL, FREE_VERSION, freeParamsOf, MAX_BACKS, MAX_SHELVES, MAX_UPRIGHTS, type Shelf, type Upright } from './design';

const parsed = parseConstructionProfile(jointsJson);
if (!parsed.ok) throw new Error(`free-grid joints.json is invalid: ${parsed.errors.join('; ')}`);
export const FREE_JOINTS: ConstructionProfile = Object.freeze(parsed.value) as ConstructionProfile;

/** Furthest a shelf held by one upright may reach past it (ticks). */
export const MAX_REACH = mm(450);
/** Narrowest gap between two parallel boards that face each other (ticks). */
export const MIN_GAP = mm(100);

const r3 = (v: number) => Math.round(v * 1000) / 1000;
const wholeMm = (ticks: number) => Math.round(ticks / 10) * 10;

interface U { i: number; xl: number; xr: number; ya: number; yb: number; side: boolean }
interface S { k: number; yl: number; yu: number; xa: number; xb: number }
interface Seg { id: string; u: number; ya: number; yb: number }

/** The resolved layout: boards and joints in ticks, plus each shelf's supports. */
export interface FreeLayout {
  shelves: S[];
  uprights: U[];
  segments: Seg[];
  joints: FamilyJoint[];
  /** Per shelf: the x of each support (an upright face it is held by), and whether it lies on the floor. */
  supports: { k: number; at: { x0: number; x1: number; u: number }[]; floor: boolean }[];
  /** Upright pieces that stand on nothing (not the floor, not a shelf). */
  hanging: string[];
}

/**
 * Resolve the junctions of a parameter block (W, H in ticks; lift = feet height; t = thickness).
 * Pure; the shelves and uprights keep their input order in `k` and `i`.
 */
export function resolveFree(uprights: readonly Upright[], shelves: readonly Shelf[], W: number, H: number, lift: number, t: number): FreeLayout {
  const Hc = H - lift;
  // 5 % of W: a shelf end read against the outline stops 3–4 % short of a side placed by its centre-line (stress-v2 v038)
  const snapX = Math.max(2 * t, Math.round(0.05 * W));
  const snapY = Math.max(2 * t, Math.round(0.03 * Hc));
  // positions in whole millimetres (cut lists and datums read in mm), kept inside the outline
  const S: S[] = shelves.map((s, k) => {
    const yl = lift + wholeMm(s.y * (Hc - t));
    return { k, yl, yu: yl + t, xa: wholeMm(s.x0 * W), xb: Math.min(W, wholeMm(s.x1 * W)) };
  });
  const U: U[] = uprights.map((u, i) => {
    const xl = Math.min(W - t, wholeMm(u.x * (W - t)));
    return { i, xl, xr: xl + t, ya: lift + wholeMm(u.y0 * Hc), yb: Math.min(H, lift + wholeMm(u.y1 * Hc)), side: xl <= 0 || xl + t >= W };
  });
  const joints: FamilyJoint[] = [];
  // shelf k butts into upright i at its end ('l' | 'r'); upright i ends on/under shelf k ('a' bottom | 'b' top)
  const shelfEnds: { k: number; i: number; end: 'l' | 'r'; corner: boolean }[] = [];
  const uprightEnds: { i: number; k: number; end: 'a' | 'b' }[] = [];
  const reaches = (u: U, s: S) => u.ya <= s.yu + snapY && u.yb >= s.yl - snapY;

  // 1. shelf ends at an upright: butt into its face; at a corner the upright covers the shelf
  for (const s of S) {
    for (const end of ['l', 'r'] as const) {
      const xe = end === 'l' ? s.xa : s.xb;
      let best: U | null = null, bestD = Infinity;
      for (const u of U) {
        if (!reaches(u, s)) continue;
        // the shelf must lie on the far side of the upright from its end
        if (end === 'l' && s.xb <= u.xr) continue;
        if (end === 'r' && s.xa >= u.xl) continue;
        const d = xe >= u.xl && xe <= u.xr ? 0 : Math.min(Math.abs(xe - u.xl), Math.abs(xe - u.xr));
        if (d <= snapX && d < bestD) { best = u; bestD = d; }
      }
      if (!best) continue;
      if (end === 'l') s.xa = best.xr; else s.xb = best.xl;
      let corner = false;
      if (Math.abs(best.yb - s.yu) <= snapY || (best.yb >= s.yl - snapY && best.yb <= s.yu)) { best.yb = s.yu; corner = true; }
      else if (Math.abs(best.ya - s.yl) <= snapY || (best.ya <= s.yu + snapY && best.ya >= s.yl)) { best.ya = s.yl; corner = true; }
      shelfEnds.push({ k: s.k, i: best.i, end, corner });
    }
  }
  const butts = (k: number, i: number) => shelfEnds.some((e) => e.k === k && e.i === i);

  // 2. upright ends: the floor, the top, or a shelf that runs on past them
  for (const u of U) {
    const covering = S.filter((s) => !butts(s.k, u.i) && s.xa <= u.xl + 1 && s.xb >= u.xr - 1);
    // top
    const over = covering.filter((s) => Math.abs(u.yb - s.yl) <= snapY || Math.abs(u.yb - s.yu) <= snapY).sort((a, b) => Math.abs(u.yb - a.yl) - Math.abs(u.yb - b.yl))[0];
    if (over) { u.yb = over.yl; uprightEnds.push({ i: u.i, k: over.k, end: 'b' }); }
    else if (H - u.yb <= snapY) u.yb = H;
    // bottom: on a shelf that runs under it (a bottom board too), else the floor
    const under = covering.filter((s) => Math.abs(u.ya - s.yu) <= snapY || Math.abs(u.ya - s.yl) <= snapY).sort((a, b) => Math.abs(u.ya - a.yu) - Math.abs(u.ya - b.yu))[0];
    if (under) { u.ya = under.yu; uprightEnds.push({ i: u.i, k: under.k, end: 'a' }); }
    else if (u.ya - lift <= snapY) u.ya = lift;
  }

  // 3. shelves that run right across an upright cut it; the pieces are housed into the shelf
  const segments: Seg[] = [];
  const crossings: { i: number; k: number }[] = [];
  for (const u of U) {
    const cuts = S.filter((s) => !butts(s.k, u.i) && !uprightEnds.some((e) => e.i === u.i && e.k === s.k)
      && s.xa < u.xl && s.xb > u.xr && u.ya < s.yl && u.yb > s.yu).sort((a, b) => a.yl - b.yl);
    cuts.forEach((s) => crossings.push({ i: u.i, k: s.k }));
    const edges = [u.ya, ...cuts.flatMap((s) => [s.yl, s.yu]), u.yb];
    const pieces = edges.length / 2;
    for (let p = 0; p < pieces; p++) {
      segments.push({ id: pieces === 1 ? `upright-${u.i + 1}` : `upright-${u.i + 1}-${p + 1}`, u: u.i, ya: edges[2 * p]!, yb: edges[2 * p + 1]! });
    }
  }
  const segAt = (i: number, y0: number, y1: number) => segments.find((g) => g.u === i && g.ya <= y0 + 1 && g.yb >= y1 - 1)
    ?? segments.filter((g) => g.u === i).sort((a, b) => Math.abs((a.ya + a.yb) / 2 - (y0 + y1) / 2) - Math.abs((b.ya + b.yb) / 2 - (y0 + y1) / 2))[0];
  const shelfId = (k: number) => `shelf-${k + 1}`;

  // joints and supports
  const supports = S.map((s) => ({ k: s.k, at: [] as { x0: number; x1: number; u: number }[], floor: s.yl <= lift + 1 }));
  for (const e of shelfEnds) {
    const s = S[e.k]!, u = U[e.i]!;
    const g = segAt(u.i, s.yl, s.yu);
    if (!g) continue;
    joints.push({ a: shelfId(s.k), b: g.id, kind: e.corner ? 'screwed-butt' : 'housed-dado', face: 'inner', datum: s.yl });
    supports[e.k]!.at.push({ x0: u.xl, x1: u.xr, u: u.i });
  }
  for (const e of uprightEnds) {
    const s = S[e.k]!, u = U[e.i]!;
    const g = e.end === 'a' ? segments.filter((x) => x.u === u.i).sort((a, b) => a.ya - b.ya)[0] : segments.filter((x) => x.u === u.i).sort((a, b) => b.yb - a.yb)[0];
    if (!g) continue;
    joints.push({ a: g.id, b: shelfId(s.k), kind: 'housed-dado', face: e.end === 'a' ? 'top' : 'underside', datum: e.end === 'a' ? s.yu : s.yl });
    // an upright under a shelf holds it up; one standing on it does not
    if (e.end === 'b') supports[e.k]!.at.push({ x0: u.xl, x1: u.xr, u: u.i });
  }
  for (const c of crossings) {
    const s = S[c.k]!;
    const below = segments.find((g) => g.u === c.i && g.yb === s.yl);
    const above = segments.find((g) => g.u === c.i && g.ya === s.yu);
    if (below) joints.push({ a: below.id, b: shelfId(s.k), kind: 'housed-dado', face: 'underside', datum: s.yl });
    if (above) joints.push({ a: above.id, b: shelfId(s.k), kind: 'housed-dado', face: 'top', datum: s.yu });
    const u = U[c.i]!;
    if (below) supports[c.k]!.at.push({ x0: u.xl, x1: u.xr, u: u.i });
  }

  // an upright piece stands on the floor, on a shelf (a joint at its foot), is held by a shelf at its foot,
  // or is clamped by two or more shelves housed into it (each held at its other end; pilot-v1-r3 p16); else it hangs
  const hanging = segments.filter((g) => g.ya > lift + 1
    && !joints.some((j) => j.a === g.id && j.face === 'top' && Math.abs(j.datum - g.ya) <= 1)
    && !joints.some((j) => j.b === g.id && Math.abs(j.datum - g.ya) <= t + 1)
    && joints.filter((j) => j.b === g.id).length < 2).map((g) => g.id);
  return { shelves: S, uprights: U, segments, joints, supports, hanging };
}

/** Board thickness and lift (feet) of a design, ticks: the frame the stored fractions are read in. */
export function freeFrame(design: Design): { t: number; lift: number } {
  const R = resolveStock(familyStock(design, null, STOCKS, 'birch-ply-18'));
  const base = baseOf(freeParamsOf(design), footKindFor(R.kind));
  return { t: R.thicknessTicks, lift: base.kind === 'feet' ? liftOf(base) : 0 };
}

export function generateFree(design: Design, stock?: AnyStock | null, construction?: ConstructionProfile): BuildModel {
  const c = familyJoints(FREE_ID, construction, FREE_JOINTS);
  const S0 = familyStock(design, stock, STOCKS, 'birch-ply-18');
  const R = resolveStock(S0);
  const t = R.thicknessTicks;
  const p = freeParamsOf(design);
  const { W, H, D } = design;
  const errors: Issue[] = sizeErrors(design, c);
  const warnings: Issue[] = [];
  const hardware: Hardware[] = [];
  const maxSpan = Math.round((c.limits?.maxSpanMm ?? c.warnings.maxShelfSpanMm) * 10);
  const ratio = c.limits?.maxCantileverRatio ?? 0.35;
  const foot = footKindFor(R.kind);
  const base = baseOf(p, foot);
  if (p.base !== undefined) errors.push(...baseErrors(base, D, foot));
  if (base.kind === 'plinth' || base.kind === 'sides') errors.push({ field: 'base', message: 'A free layout stands on the floor or on feet.', fix: { label: 'Stand it on the floor', patch: { base: 'none' } } });
  const lift = base.kind === 'feet' ? liftOf(base) : 0;
  if (p.shelves.length > MAX_SHELVES) errors.push({ field: 'shelves', message: `At most ${MAX_SHELVES} shelves.` });
  if (p.uprights.length > MAX_UPRIGHTS) errors.push({ field: 'uprights', message: `At most ${MAX_UPRIGHTS} uprights.` });
  if (!p.shelves.length) errors.push({ field: 'shelves', message: 'A shelf needs at least one horizontal board.' });

  const boards: FamilyBoard[] = [];
  let joints: FamilyJoint[] = [];
  const openings: FamilyOpening[] = [];
  const datums: { partId: string; label: string; ticks: number }[] = [];
  let shelfCount = 0;
  if (layable(design) && p.shelves.length) {
    const L = resolveFree(p.uprights, p.shelves, W, H, lift, t);
    joints = L.joints;
    // with back boards, the shelves and uprights stand one board thickness forward of the back plane
    const backs = p.backs ?? [];
    const bz = backs.length ? t : 0;
    const zF = bz, dF = D - bz;
    const pctW = (x: number) => `${fmtMm(x)} mm`;
    const removeShelf = (k: number) => ({ shelves: p.shelves.filter((_s, j) => j !== k) });
    const removeUpright = (i: number) => ({ uprights: p.uprights.filter((_u, j) => j !== i) });
    // horizontals, numbered bottom-up as the photo reads them
    const order = L.shelves.slice().sort((a, b) => a.yl - b.yl || a.xa - b.xa);
    const label = new Map<number, string>();
    const floors = order.filter((q) => q.yl <= lift + 1).length;
    const tops = order.filter((q) => q.yl > lift + 1 && q.yu >= H - 1).length;
    let nb = 0, nt = 0, ns = 0;
    for (const s of order) {
      if (s.yl <= lift + 1) { nb++; label.set(s.k, floors > 1 ? `Bottom ${nb}` : 'Bottom'); }
      else if (s.yu >= H - 1) { nt++; label.set(s.k, tops > 1 ? `Top ${nt}` : 'Top'); }
      else { ns++; label.set(s.k, `Shelf ${ns}`); }
    }
    for (const s of L.shelves) {
      const len = s.xb - s.xa;
      const name = label.get(s.k)!;
      if (len < 10) { errors.push({ field: `shelf-${s.k}`, message: `${name} has no length left between the uprights it meets.`, fix: { label: `Remove ${name.toLowerCase()}`, patch: removeShelf(s.k) } }); continue; }
      const role = s.yl <= lift + 1 ? 'bottom' : s.yu >= H - 1 ? 'top' : 'shelf';
      boards.push({ id: `shelf-${s.k + 1}`, role, label: name, l: len, w: dF, t, grainWorld: 'x', box: { x: s.xa, y: s.yl, z: zF, sx: len, sy: t, sz: dF }, stock: 'carcass', datum: s.yl });
      datums.push({ partId: `shelf-${s.k + 1}`, label: name, ticks: s.yl });
      if (role === 'shelf') shelfCount++;
    }
    const segsOf = (i: number) => L.segments.filter((g) => g.u === i);
    // inner uprights numbered left to right, as the photo reads them
    const inner = L.uprights.filter((u) => u.xl > 0 && u.xr < W).sort((a, b) => a.xl - b.xl || a.ya - b.ya).map((u) => u.i);
    for (const u of L.uprights) {
      const segs = segsOf(u.i);
      const where = u.xl <= 0 ? 'Left side' : u.xr >= W ? 'Right side' : inner.length > 1 ? `Upright ${inner.indexOf(u.i) + 1}` : 'Upright';
      segs.forEach((g, n) => {
        const len = g.yb - g.ya;
        const name = segs.length > 1 ? `${where}, piece ${n + 1} of ${segs.length}` : where;
        if (len < 10) { errors.push({ field: `upright-${u.i}`, message: `${name} has no height left between the shelves it meets.`, fix: { label: `Remove ${where.toLowerCase()}`, patch: removeUpright(u.i) } }); return; }
        boards.push({ id: g.id, role: u.side ? 'side' : 'divider', label: name, l: len, w: dF, t, grainWorld: 'y', box: { x: u.xl, y: g.ya, z: zF, sx: t, sy: len, sz: dF }, stock: 'carcass', faceSide: 'out' });
      });
    }

    // back boards (pilot-v1-r3 p16): in the back plane, screwed onto the back edges of every board they cover
    const Hc = H - lift;
    const snapY = Math.max(2 * t, Math.round(0.03 * Hc));
    const removeBack = (b: number) => ({ backs: backs.filter((_k, j) => j !== b) });
    backs.forEach((k, b) => {
      let x0 = wholeMm(k.x0 * W), x1 = wholeMm(k.x1 * W), y0 = lift + wholeMm(k.y0 * Hc), y1 = lift + wholeMm(k.y1 * Hc);
      // top and bottom edges near a shelf cover it (the back is screwed onto it; pilot-v1-r4 p16) and near the floor or
      // the top reach them; the side edges keep their reading (a back set between the sides leaves a gap, p16 r4b),
      // reaching the outline only within half a board
      const horizontals = boards.filter((q) => q.grainWorld === 'x' && q.role !== 'back');
      const near = (v: number, faces: number[], reach: number) => faces.filter((f) => Math.abs(v - f) <= reach).sort((a, c) => Math.abs(v - a) - Math.abs(v - c))[0];
      const inX = (q: FamilyBoard) => Math.min(q.box.x + q.box.sx, x1) - Math.max(q.box.x, x0) > 0;
      const b0 = near(y0, horizontals.filter(inX).flatMap((q) => [q.box.y, q.box.y + q.box.sy]), snapY);
      if (b0 !== undefined) y0 = horizontals.find((q) => q.box.y === b0 || q.box.y + q.box.sy === b0)!.box.y;
      const t1 = near(y1, horizontals.filter(inX).flatMap((q) => [q.box.y, q.box.y + q.box.sy]), snapY);
      if (t1 !== undefined) { const q = horizontals.find((v) => v.box.y === t1 || v.box.y + v.box.sy === t1)!; y1 = q.box.y + q.box.sy; }
      if (x0 <= t / 2) x0 = 0;
      if (W - x1 <= t / 2) x1 = W;
      if (y0 - lift <= snapY) y0 = lift;
      if (H - y1 <= snapY) y1 = H;
      const id = `back-${b + 1}`;
      const name = backs.length > 1 ? `Back board ${b + 1}` : 'Back board';
      const w = x1 - x0, h = y1 - y0;
      if (w < MIN_GAP / 2 || h < MIN_GAP / 2) { errors.push({ field: 'backs', message: `${name} is ${fmtMm(w)} × ${fmtMm(h)} mm; a back board is at least ${fmtMm(MIN_GAP / 2)} mm each way.`, fix: { label: `Remove ${name.toLowerCase()}`, patch: removeBack(b) } }); return; }
      const vertical = h >= w;
      boards.push({ id, role: 'back', label: name, l: vertical ? h : w, w: vertical ? w : h, t: bz, grainWorld: vertical ? 'y' : 'x', box: { x: x0, y: y0, z: 0, sx: w, sy: h, sz: bz }, stock: 'carcass', faceSide: 'out', machining: 'screwed onto the back edges it covers' });
      const covered = boards.filter((q) => q.role !== 'back' && Math.min(q.box.x + q.box.sx, x1) - Math.max(q.box.x, x0) > 0 && Math.min(q.box.y + q.box.sy, y1) - Math.max(q.box.y, y0) > 0);
      if (!covered.length) errors.push({ field: 'backs', message: `${name} covers no shelf or upright, so nothing holds it.`, fix: { label: `Remove ${name.toLowerCase()}`, patch: removeBack(b) } });
      for (const q of covered) joints.push({ a: id, b: q.id, kind: 'screwed-butt', face: 'back', datum: Math.max(q.box.y, y0) });
    });
    const ids = new Set(boards.map((b) => b.id));
    joints = joints.filter((j) => ids.has(j.a) && ids.has(j.b));
    if (backs.length > MAX_BACKS) errors.push({ field: 'backs', message: `At most ${MAX_BACKS} back boards.` });

    // every upright piece stands on something
    for (const id of L.hanging) {
      if (!ids.has(id)) continue;
      const i = Number(id.split('-')[1]) - 1;
      errors.push({ field: `upright-${i}`, message: `${boards.find((b) => b.id === id)!.label} stands on nothing: take it down to the floor or onto a shelf.`, fix: { label: 'Take it down to the floor', patch: { uprights: p.uprights.map((u, j) => (j === i ? { ...u, y0: 0 } : u)) } } });
    }

    // every shelf is held: spans, overhangs, spine cantilevers
    const spine: string[] = [];
    for (const sup of L.supports) {
      const s = L.shelves[sup.k]!;
      const name = label.get(s.k)!;
      if (!ids.has(`shelf-${s.k + 1}`)) continue;
      if (sup.floor) continue; // a bottom board on the floor (or the feet) is held along its length
      const at = sup.at.slice().sort((a, b) => a.x0 - b.x0);
      if (!at.length) {
        errors.push({ field: `shelf-${s.k}`, message: `${name} is not held by any upright.`, fix: { label: `Remove ${name.toLowerCase()}`, patch: removeShelf(s.k) } });
        continue;
      }
      const left = s.xa < at[0]!.x0 ? at[0]!.x0 - s.xa : 0;
      const right = s.xb > at[at.length - 1]!.x1 ? s.xb - at[at.length - 1]!.x1 : 0;
      const shorten = (newLeft: number, newRight: number) => {
        const sh = p.shelves[s.k]!;
        return { shelves: p.shelves.map((q, j) => (j === s.k ? { ...sh, x0: r3(Math.max(0, (at[0]!.x0 - newLeft) / W)), x1: r3(Math.min(1, (at[at.length - 1]!.x1 + newRight) / W)) } : q)) };
      };
      if (new Set(at.map((a) => a.u)).size === 1) {
        // held by one upright: a spine shelf, a rated cantilever
        const reach = Math.max(left, right);
        if (reach > MAX_REACH) {
          errors.push({ field: `shelf-${s.k}`, message: `${name} reaches ${pctW(reach)} past the one upright that holds it; a spine shelf may reach at most ${pctW(MAX_REACH)}.`, fix: { label: `Shorten ${name.toLowerCase()} to ${fmtMm(MAX_REACH)} mm`, patch: shorten(Math.min(left, MAX_REACH), Math.min(right, MAX_REACH)) } });
        }
        spine.push(`shelf-${s.k + 1}`);
        continue;
      }
      // two or more supports: spans between them, overhangs past the outer ones
      for (let n = 1; n < at.length; n++) {
        const span = at[n]!.x0 - at[n - 1]!.x1;
        if (span > maxSpan) {
          const mid = (at[n]!.x0 + at[n - 1]!.x1) / 2;
          const under = L.shelves.filter((q) => q.yu <= s.yl && q.xa <= mid && q.xb >= mid).sort((a, b) => b.yu - a.yu)[0];
          const y0 = under ? r3((under.yu - lift) / (H - lift)) : 0;
          const fix = p.uprights.length < MAX_UPRIGHTS ? { label: `Add an upright under ${name.toLowerCase()}`, patch: { uprights: [...p.uprights, { x: r3((mid - t / 2) / (W - t)), y0, y1: r3((s.yl - lift) / (H - lift)) }] } } : undefined;
          warnings.push({ field: `shelf-${s.k}`, message: `${name} spans ${pctW(span)} between uprights. Under a full load of books it may sag; keep spans under ${pctW(maxSpan)}.`, ...(fix ? { fix } : {}) });
        }
      }
      const outer = Math.max(at[at.length - 1]!.x1 - at[0]!.x0, 1);
      const maxOver = Math.round(ratio * outer);
      if (left > maxOver || right > maxOver) {
        errors.push({ field: `shelf-${s.k}`, message: `${name} overhangs its outer upright by ${pctW(Math.max(left, right))}; at most ${pctW(maxOver)} (${Math.round(ratio * 100)} % of the span between its supports).`, fix: { label: `Shorten the overhang to ${fmtMm(maxOver)} mm`, patch: shorten(Math.min(left, maxOver), Math.min(right, maxOver)) } });
      }
    }
    if (spine.length) {
      // a hidden steel bracket (two floating-shelf rods) set into the upright under each spine shelf
      hardware.push({
        kind: 'cantilever-bracket', qty: spine.length * 2, visible: false,
        spec: 'Hidden steel shelf bracket: two 12 mm floating-shelf rods per shelf, set 120 mm into the upright and the shelf',
        at: spine.flatMap((id) => {
          const b = boards.find((q) => q.id === id)!;
          return [dF / 3, (2 * dF) / 3].map((z) => ({ partId: id, x: (b.box.x + b.box.sx / 2) / 10, y: (b.box.y + t / 2) / 10, z: (zF + z) / 10 }));
        }),
      });
      warnings.push({ field: 'shelves', message: `${spine.length === 1 ? 'One shelf hangs' : `${spine.length} shelves hang`} from a single upright: each is housed deep with hidden steel brackets, and the piece must be fixed to the wall before loading.` });
    }

    // boards facing each other too close to use
    const hs = boards.filter((b) => b.role !== 'back' && b.grainWorld === 'x');
    for (let a = 0; a < hs.length; a++) for (let b = a + 1; b < hs.length; b++) {
      const A = hs[a]!.box, B = hs[b]!.box;
      const ox = Math.min(A.x + A.sx, B.x + B.sx) - Math.max(A.x, B.x);
      const gap = Math.max(A.y, B.y) - Math.min(A.y + A.sy, B.y + B.sy);
      if (ox > t && gap > 0 && gap < MIN_GAP) {
        // fix: move the upper board up to the minimum gap
        // fix: move the upper board up to the minimum gap, or, when it is the top, the lower one down (stress-v2 v039)
        const upper = A.y > B.y ? hs[a]! : hs[b]!;
        const lower = upper === hs[a] ? hs[b]! : hs[a]!;
        const kU = Number(upper.id.split('-')[1]) - 1, kL = Number(lower.id.split('-')[1]) - 1;
        // 1 mm over the gap, so rounding to whole millimetres never leaves it 1 mm short
        const by = MIN_GAP - gap + 10;
        const yUp = r3((upper.box.y + by - lift) / (H - lift - t));
        const yDown = r3((lower.box.y - by - lift) / (H - lift - t));
        const fix = yUp <= 1 && upper.role !== 'top'
          ? { label: `Move ${upper.label.toLowerCase()} up ${fmtMm(by)} mm`, patch: { shelves: p.shelves.map((q, j) => (j === kU ? { ...q, y: yUp } : q)) } }
          : yDown >= 0 ? { label: `Move ${lower.label.toLowerCase()} down ${fmtMm(by)} mm`, patch: { shelves: p.shelves.map((q, j) => (j === kL ? { ...q, y: yDown } : q)) } } : undefined;
        errors.push({ field: 'shelves', message: `${hs[a]!.label} and ${hs[b]!.label} are only ${pctW(gap)} apart; leave at least ${pctW(MIN_GAP)}.`, ...(fix ? { fix } : {}) });
      }
    }
    const vs = boards.filter((b) => b.role !== 'back' && b.grainWorld === 'y');
    for (let a = 0; a < vs.length; a++) for (let b = a + 1; b < vs.length; b++) {
      const A = vs[a]!.box, B = vs[b]!.box;
      const oy = Math.min(A.y + A.sy, B.y + B.sy) - Math.max(A.y, B.y);
      const gap = Math.max(A.x, B.x) - Math.min(A.x + A.sx, B.x + B.sx);
      if (oy > t && gap > 0 && gap < MIN_GAP) {
        // fix: move the right-hand upright right to the minimum gap
        const right = A.x > B.x ? vs[a]! : vs[b]!;
        const i = Number(right.id.split('-')[1]) - 1;
        const x = r3((right.box.x + (MIN_GAP - gap)) / (W - t));
        const fix = x <= 1 ? { label: `Move ${right.label.replace(/, piece \d+ of \d+$/, '').toLowerCase()} right ${fmtMm(MIN_GAP - gap)} mm`, patch: { uprights: p.uprights.map((u, j) => (j === i ? { ...u, x } : u)) } } : undefined;
        errors.push({ field: 'uprights', message: `${vs[a]!.label} and ${vs[b]!.label} are only ${pctW(gap)} apart; leave at least ${pctW(MIN_GAP)}.`, ...(fix ? { fix } : {}) });
      }
    }

    // everything connects to the floor
    const parent = new Map<string, string>();
    const find = (x: string): string => { while (parent.get(x) !== x) { parent.set(x, parent.get(parent.get(x)!)!); x = parent.get(x)!; } return x; };
    const union = (a: string, b: string) => parent.set(find(a), find(b));
    parent.set('floor', 'floor');
    for (const b of boards) parent.set(b.id, b.id);
    for (const j of joints) union(j.a, j.b);
    for (const b of boards) if (b.box.y <= lift + 1) union(b.id, 'floor');
    for (const b of boards) {
      if (find(b.id) === find('floor')) continue;
      errors.push({ field: b.id.startsWith('shelf') ? 'shelves' : 'uprights', message: `${b.label} does not connect to the rest of the shelf.` });
    }

    // openings: above each shelf, between the uprights standing on it, up to the next board above
    for (const s of order) {
      if (!ids.has(`shelf-${s.k + 1}`)) continue;
      const walls = vs.filter((v) => v.box.y <= s.yu + 1 && v.box.y + v.box.sy > s.yu + 1 && v.box.x + v.box.sx > s.xa && v.box.x < s.xb).map((v) => v.box).sort((a, b) => a.x - b.x);
      const edges: [number, number][] = [];
      let at = s.xa;
      for (const w of walls) { if (w.x > at) edges.push([at, w.x]); at = Math.max(at, w.x + w.sx); }
      if (at < s.xb) edges.push([at, s.xb]);
      edges.forEach(([x0, x1], n) => {
        if (x1 - x0 < MIN_GAP / 2) return;
        const mid = (x0 + x1) / 2;
        const ceiling = hs.filter((h) => h.box.y >= s.yu && h.box.x <= mid && h.box.x + h.box.sx >= mid).sort((a, b) => a.box.y - b.box.y)[0];
        if (!ceiling) return; // open above (the top of an E or a spine): not an opening
        openings.push({ id: `cell-${s.k + 1}-${n + 1}`, label: `${label.get(s.k)}, ${edges.length > 1 ? `cell ${n + 1}` : 'opening'}`, x0, x1, y0: s.yu, y1: ceiling.box.y });
      });
    }

    if (base.kind === 'feet') {
      // feet at the two ends of each run of boards standing side by side on the base (a side and the
      // bottom between two sides are one run), plus the board at the middle of a run over 1200 mm
      const floor = boards.filter((b) => b.role !== 'back' && b.box.y <= lift + 1).sort((a, b) => a.box.x - b.box.x);
      const runs: (typeof floor)[] = [];
      for (const b of floor) {
        const run = runs[runs.length - 1];
        const end = run ? Math.max(...run.map((q) => q.box.x + q.box.sx)) : -Infinity;
        if (run && b.box.x <= end + 1) run.push(b); else runs.push([b]);
      }
      const under: { id: string; x0: number; x1: number; z0: number; z1: number }[] = [];
      for (const run of runs) {
        const x0 = run[0]!.box.x, x1 = Math.max(...run.map((q) => q.box.x + q.box.sx));
        const at = (x: number) => run.find((q) => q.box.x <= x && q.box.x + q.box.sx >= x) ?? run[0]!;
        const picks = new Map<string, { x0: number; x1: number }>();
        // the end boards, each over at most its last 100 mm (a side: one foot at its centre; a bottom: one near its end)
        const add = (b: (typeof run)[number], lo: number, hi: number) => picks.set(`${b.id}@${lo}`, { x0: lo, x1: hi });
        const left = at(x0), right = at(x1);
        add(left, left.box.x, Math.min(left.box.x + left.box.sx, x0 + 1000));
        if (right !== left || x1 - x0 > 1000) add(right, Math.max(right.box.x, x1 - 1000), x1);
        if (x1 - x0 > 12000) { const m = at((x0 + x1) / 2); add(m, (x0 + x1) / 2 - 500, (x0 + x1) / 2 + 500); }
        for (const [key, r] of picks) under.push({ id: key.split('@')[0]!, x0: r.x0, x1: r.x1, z0: 0, z1: D });
      }
      hardware.push(...feetHardware(lift, under, foot));
    }
  }
  if (H > Math.round(c.warnings.antiTipAboveMm * 10)) warnings.push({ field: 'H', message: `At ${fmtMm(H)} mm tall this must be fixed to the wall (anti-tip).` });
  return assembleBuild({
    familyId: FREE_ID, familyVersion: FREE_VERSION, familyLabel: FREE_LABEL, design, stock: S0, construction: c,
    boards, joints, openings, datums, errors, warnings, hardware, clearWidth: W - 2 * t, shelves: shelfCount,
  });
}
